import React, { useEffect, useState } from "react";
import moment from "moment";
import api from "../../api";
import { Modal } from "react-bootstrap";
import { notify } from "../../utils/toast";
import { Pagination, Stack } from "@mui/material";

const LIMIT = 10;

export const Loans = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [show, setShow] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const openEdit = (item) => setEditItem(item);
  const closeEdit = () => setEditItem(null);

  const getData = async (pageVal = page) => {
    try {
      setLoader(true);
      const response = await api.getLoan({ page: pageVal, limit: LIMIT });
      if (response?.success) {
        setData(response?.loans || []);
        setTotal(response?.total || 0);
        setTotalPages(response?.totalPages || 1);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { getData(1); }, []);

  const handlePageChange = (e, value) => { setPage(value); getData(value); };

  const deleteLoan = async (id) => {
    if (!window.confirm("Are you sure you want to delete this loan record?")) return;
    try {
      const response = await api.deleteLoan(id);
      if (response?.success) {
        notify.success("Loan deleted successfully!");
        getData(page);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    }
  };

  return (
    <>
      <div className="DashboardMain">
        <div className="row">
          <div className="col">
            <h3>Loans ({total})</h3>
            {loader ? (
              <div className="text-center py-5"><div className="spinner-border" role="status" /></div>
            ) : (
              <Table data={data} deleteLoan={deleteLoan} openEdit={openEdit} />
            )}
            {data && data.length > 0 && total > LIMIT && (
              <div className="pagination-container">
                <Stack spacing={2}>
                  <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#10b981", color: "white", "&:hover": { backgroundColor: "#059669" } },
                      "& .MuiPaginationItem-root": { "&:hover": { backgroundColor: "#d1fae5", color: "#059669" } },
                    }}
                  />
                </Stack>
              </div>
            )}
          </div>
        </div>
        <div className="AddEmpBtn" onClick={handleShow}><i className="fa-solid fa-plus"></i></div>
      </div>
      <GenerateModal show={show} handleClose={handleClose} setLoader={setLoader} getData={() => getData(page)} />
      {editItem && <EditLoanModal item={editItem} handleClose={closeEdit} getData={() => getData(page)} />}
    </>
  );
};

const Table = ({ data, deleteLoan, openEdit }) => {
  const formatDate = (date) => moment(date).format("MMM Do YYYY, h:mm a");
  return (
    <div className="TableMain mt-3">
      <div className="table-responsive">
        <table className="table table-hover drivers-table">
          <thead>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>Employee / Driver</th>
              <th style={{ whiteSpace: "nowrap" }}>Amount</th>
              <th style={{ whiteSpace: "nowrap" }}>Paid</th>
              <th style={{ whiteSpace: "nowrap" }}>Remaining</th>
              <th>Reason</th>
              <th>Type</th>
              <th>Status</th>
              <th style={{ whiteSpace: "nowrap" }}>Created At</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, key) => {
              // paidAmount field exists on new records. For legacy "deducted" loans
              // where paidAmount was 0 (didn't exist before), treat as fully paid.
              const effectivePaid = (item?.paidAmount || 0) > 0
                ? item.paidAmount
                : (item?.status === "deducted" ? item?.amount : 0);
              const remaining = Math.max(0, (item?.amount || 0) - effectivePaid);
              return (
              <tr key={key}>
                <td>
                  <div className="table-text-dark w-max-c db" style={{ textTransform: "capitalize" }}>
                    {item?.driverId?.name || "—"}
                  </div>
                </td>
                <td>
                  <span className="table-text-dark w-max-c db" style={{ fontWeight: 700, color: "#991b1b" }}>
                    Rs. {item?.amount?.toLocaleString() || "0"}
                  </span>
                </td>
                <td>
                  <span className="table-text-dark w-max-c db" style={{ fontWeight: 600, color: "#15803d" }}>
                    {effectivePaid > 0 ? `Rs. ${effectivePaid.toLocaleString()}` : "—"}
                  </span>
                </td>
                <td>
                  <span className="table-text-dark w-max-c db" style={{ fontWeight: 600, color: remaining > 0 ? "#b45309" : "#15803d" }}>
                    Rs. {remaining.toLocaleString()}
                  </span>
                </td>
                <td>
                  <span className="table-text-dark db" style={{ textTransform: "capitalize" }}>
                    {item?.reason || "—"}
                  </span>
                </td>
                <td>
                  <span className="table-text-dark w-max-c db" style={{ textTransform: "capitalize" }}>
                    {item?.type || "—"}
                  </span>
                </td>
                <td>
                  <span style={{
                    display: "inline-block",
                    padding: "3px 10px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "capitalize",
                    background: item?.status === "deducted" ? "#d1fae5" : "#fee2e2",
                    color: item?.status === "deducted" ? "#065f46" : "#991b1b",
                  }}>
                    {item?.status || "—"}
                  </span>
                </td>
                <td>
                  <span className="table-text-dark w-max-c db" style={{ fontSize: 13 }}>
                    {formatDate(item?.createdAt)}
                  </span>
                </td>
                <td>
                  <div className="d-flex gap-2 align-items-center">
                    <div className="edit p-2 cursor-pointer" onClick={() => openEdit(item)}>
                      <i className="fa-solid fa-pen-to-square"></i>
                    </div>
                    <div className="delete p-2 cursor-pointer" onClick={() => deleteLoan(item?._id)}>
                      <i className="fa-solid fa-trash"></i>
                    </div>
                  </div>
                </td>
              </tr>
            ); })} {/* end map */}
            {data.length === 0 && (
              <tr><td colSpan={9} className="text-center py-4 text-muted">No loan records found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GenerateModal = ({ show, handleClose, setLoader, getData }) => {
  const [drivers, setDrivers] = useState([]);
  const [data, setData] = useState({});
  const onTextChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    try {
      const payload = { driverId: data?.driverId, amount: data?.amount, reason: data?.reason };
      const response = await api.addLoan(payload);
      if (response?.success) {
        notify.success("Loan amount successfully added!");
        getData();
        setData({});
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  const getDriverData = async () => {
    try {
      setLoader(true);
      const response = await api.getDrivers();
      if (response?.success) setDrivers(response?.drivers || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { getDriverData(); }, []);

  return (
    <Modal show={show} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}><i className="fa-solid fa-xmark" /></div>
        <div className="h3">Add Loan</div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <label>Select Driver</label>
              <select name="driverId" className="form-control" onChange={onTextChange}>
                <option value="">Select</option>
                {drivers.map((item, key) => <option value={item?._id} key={key}>{item?.name}</option>)}
              </select>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Amount</label>
              <input type="number" name="amount" className="form-control" value={data?.amount || ""} onChange={onTextChange} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Reason</label>
              <textarea name="reason" className="form-control" value={data?.reason || ""} onChange={onTextChange}></textarea>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>Add Loan</button>
        </div>
      </div>
    </Modal>
  );
};

const EditLoanModal = ({ item, handleClose, getData }) => {
  const [data, setData] = useState({
    amount: item?.amount || "",
    reason: item?.reason || "",
    status: item?.status || "unpaid",
  });
  const onTextChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    try {
      const response = await api.updateLoan(item._id, data);
      if (response?.success) {
        notify.success("Loan updated successfully!");
        getData();
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  return (
    <Modal show={true} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}><i className="fa-solid fa-xmark" /></div>
        <div className="h3">Edit Loan</div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <label>Employee/Driver</label>
              <input type="text" className="form-control" value={item?.driverId?.name || ""} disabled />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Amount</label>
              <input type="number" name="amount" className="form-control" value={data?.amount} onChange={onTextChange} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Reason</label>
              <textarea name="reason" className="form-control" value={data?.reason} onChange={onTextChange}></textarea>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Status</label>
              <select name="status" className="form-control" value={data?.status} onChange={onTextChange}>
                <option value="unpaid">Unpaid</option>
                <option value="deducted">Deducted</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>Update</button>
        </div>
      </div>
    </Modal>
  );
};
